// ========================================
// VISUAL AGENT - FASE 6
// Generador de Imágenes Educativas (Cartoon Style)
// ========================================

/**
 * Agente Visual
 * Genera imágenes educativas estilo cartoon usando Imagen API de Google.
 * Aplica master prompt para consistencia visual en toda la secuencia.
 */

/**
 * Configuración del Agente Visual
 */
const VISUAL_AGENT_CONFIG = {
    // Endpoint de Imagen API (usar Imagen 3.0-002 en GA desde Feb 2025)
    // NOTA: Verificar disponibilidad en Google AI Studio
    endpoint: 'https://generativelanguage.googleapis.com/v1beta/models/imagen-3.0-generate-002:predict',

    // Master prompt para consistencia
    masterPromptTemplate: `Flat vector style, child-friendly cartoon illustration, clean lines, vibrant solid colors, soft lighting, simple and clear background. NO text, NO numbers, NO visual noise. HIGH QUALITY educational image. Content: {CONTENT}`,

    // Configuración de generación
    aspectRatio: '4:3', // Landscape apropiado para pantallas
    sampleCount: 1,

    // Timeout para cada imagen (ms)
    timeout: 15000,

    // Placeholder si falla
    placeholderEnabled: true
};

/**
 * Genera imágenes para todos los problemas en paralelo
 * @param {Array} problemsWithNarrative - Problemas ya enriquecidos con narrativa
 * @returns {Promise<Array>} Problemas con campo imageUrl añadido
 */
async function generateImagesForProblems(problemsWithNarrative) {
    console.log(`🎨 [VisualAgent] Iniciando generación de ${problemsWithNarrative.length} imágenes...`);

    // Verificar configuración de API
    if (!isImagenApiAvailable()) {
        console.warn('⚠️ [VisualAgent] Imagen API no disponible, usando placeholders');
        return applyPlaceholders(problemsWithNarrative);
    }

    try {
        // Generar imágenes en paralelo con Promise.all
        const imagePromises = problemsWithNarrative.map((problem, index) =>
            generateSingleImage(problem, index + 1)
        );

        const results = await Promise.all(imagePromises);

        const successCount = results.filter(r => r.imageUrl !== null).length;
        console.log(`✅ [VisualAgent] ${successCount}/${results.length} imágenes generadas exitosamente`);

        return results;

    } catch (error) {
        console.error('❌ [VisualAgent] Error en generación masiva:', error);
        return applyPlaceholders(problemsWithNarrative);
    }
}

/**
 * Genera una imagen individual
 * @param {Object} problem - Problema con image_prompt
 * @param {number} index - Índice del problema (para logging)
 * @returns {Promise<Object>} Problema con imageUrl añadido
 */
async function generateSingleImage(problem, index) {
    const startTime = Date.now();
    console.log(`🎨 [VisualAgent] Generando imagen ${index}/5...`);

    try {
        const imagePrompt = problem.image_prompt || generateFallbackImagePrompt(problem);
        const masterPrompt = buildMasterPrompt(imagePrompt);

        const imageUrl = await callImagenAPI(masterPrompt);

        const elapsed = Date.now() - startTime;
        if (imageUrl) {
            console.log(`✅ [VisualAgent] Imagen ${index} generada en ${elapsed}ms`);
            return { ...problem, imageUrl };
        } else {
            console.warn(`⚠️ [VisualAgent] Imagen ${index} falló, usando placeholder`);
            return { ...problem, imageUrl: null };
        }

    } catch (error) {
        console.error(`❌ [VisualAgent] Error en imagen ${index}:`, error.message);
        return { ...problem, imageUrl: null };
    }
}

/**
 * Construye el master prompt con plantilla de consistencia
 */
function buildMasterPrompt(contentPrompt) {
    return VISUAL_AGENT_CONFIG.masterPromptTemplate.replace('{CONTENT}', contentPrompt);
}

/**
 * Llama a la API de Imagen de Google
 * @param {string} prompt - Prompt de imagen (ya con master template)
 * @returns {Promise<string|null>} URL de imagen en base64 o null si falla
 */
async function callImagenAPI(prompt) {
    // Verificar si tenemos acceso a la API
    if (!GEMINI_CONFIG || !GEMINI_CONFIG.apiKey || GEMINI_CONFIG.apiKey === 'YOUR_API_KEY_HERE') {
        throw new Error('API key no configurada');
    }

    const url = `${VISUAL_AGENT_CONFIG.endpoint}?key=${GEMINI_CONFIG.apiKey}`;

    const requestBody = {
        instances: [{
            prompt: prompt
        }],
        parameters: {
            sampleCount: VISUAL_AGENT_CONFIG.sampleCount,
            aspectRatio: VISUAL_AGENT_CONFIG.aspectRatio
        }
    };

    // Fetch con timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), VISUAL_AGENT_CONFIG.timeout);

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestBody),
            signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(`Imagen API Error: ${response.status} - ${errorData.error?.message || 'Unknown'}`);
        }

        const data = await response.json();

        // Extraer imagen en base64
        const imageBase64 = data.predictions?.[0]?.bytesBase64Encoded;

        if (imageBase64) {
            return `data:image/png;base64,${imageBase64}`;
        } else {
            console.warn('⚠️ Respuesta de Imagen API sin datos de imagen');
            return null;
        }

    } catch (error) {
        clearTimeout(timeoutId);

        if (error.name === 'AbortError') {
            throw new Error('Timeout generando imagen');
        }
        throw error;
    }
}

/**
 * Verifica si Imagen API está disponible
 */
function isImagenApiAvailable() {
    // Por ahora, asumimos que si Gemini está configurado, Imagen también
    // En producción, se podría hacer una llamada de verificación
    return typeof GEMINI_CONFIG !== 'undefined' &&
        GEMINI_CONFIG.apiKey !== 'YOUR_API_KEY_HERE';
}

/**
 * Aplica placeholders a todos los problemas
 */
function applyPlaceholders(problems) {
    console.log('🖼️ [VisualAgent] Aplicando placeholders visuales');
    return problems.map(problem => ({
        ...problem,
        imageUrl: null // null indica usar placeholder CSS
    }));
}

/**
 * Genera un prompt de imagen básico como fallback
 */
function generateFallbackImagePrompt(problem) {
    // Generar prompt básico directamente
    const obj = problem.required_object_type || 'objects';
    const qty = problem.operands?.[0] || 5;

    return `${qty} colorful ${obj}, child-friendly cartoon illustration, educational style, simple background`;
}

/**
 * Genera imagen individual (versión secuencial, para debugging)
 * @param {string} imagePrompt - Prompt de imagen
 * @returns {Promise<string|null>} URL de imagen o null
 */
async function generateCartoonImage(imagePrompt) {
    const masterPrompt = buildMasterPrompt(imagePrompt);
    return await callImagenAPI(masterPrompt);
}

// Exportar para uso global
if (typeof window !== 'undefined') {
    window.generateImagesForProblems = generateImagesForProblems;
    window.generateCartoonImage = generateCartoonImage;
    window.VISUAL_AGENT_CONFIG = VISUAL_AGENT_CONFIG;
}

console.log('✅ [VisualAgent] Módulo cargado');
